<script setup lang="ts">
import { computed } from 'vue';
import RecordLink from '@/components/RecordLink.vue';
import { entityDisplayNameFor } from '@/composables/entityColumns';
import { listItemText } from '@/components/record/fields/formatFieldValue';
import type { TableColumnStaticProps } from '@/types/tableTypes';

/**
 * A related record: its name, linked, with the side-peek button floating left
 * of it. A list of related records does not come here. It uses the table's
 * cell, a count and a few names, whose count opens the whole list with a
 * search box, because a list can hold 500 records (see PropertyValue).
 *
 * The record type comes from the field's model: a `Location` links to a
 * location, whatever the field is called.
 */
const props = defineProps<{
    column: TableColumnStaticProps;
    value: unknown;
}>();

const relatedEntityId = computed(() => props.column.dataType.replace('[]', ''));
const relatedNoun = computed(() => entityDisplayNameFor(relatedEntityId.value));

const item = computed(() => {
    if (props.value === null || props.value === undefined) return null;
    const record = typeof props.value === 'object' ? (props.value as Record<string, unknown>) : null;
    const id = record && (typeof record.id === 'number' || typeof record.id === 'string') ? record.id : null;
    return { id, name: listItemText(props.value) };
});
</script>

<template>
  <span v-if="item" class="inline min-w-0" data-testid="related-record-value">
    <RecordLink
      v-if="item.id !== null"
      :entity-id="relatedEntityId"
      :record-id="item.id"
      :label="`${relatedNoun} ${item.name}`"
    >{{ item.name }}</RecordLink>
    <span v-else>{{ item.name }}</span>
  </span>
</template>
